import React, { useEffect, useMemo } from "react";

import { MdmDeviceDto, MdmObjectDto, SortDirection, useGetDevicesPagedLazyQuery, useGetDevicesPagedQuery } from "gql/generated/types";
import { DataTablePFSEvent } from "primereact/datatable";
import { ProgressSpinner } from "primereact/progressspinner";
import { TabPanel, TabView } from "primereact/tabview";
import { Link } from "react-router-dom";
import { T } from "services/lang/LanguageService";
import { DEFAULT_DATE_FORMATTER } from "utils/constants";
import { parseToLocalZoneDateTime } from "utils/time-utils";
import MapWrapper from "../components/MapWrapper";
import ObjectAndDeviceTableWrapper from "../components/ObjectAndDeviceTableWrapper";
import ObjectMapContent from "../components/ObjectMapContent";
import useProjectContext from "../hooks/useProjectContext";
import useTableFilter from "../hooks/useTableFilter";
import useUserTableDetails from "../hooks/useUserTableDetails";
import { measurementOrder } from "../utils/const";
import { TableColumn } from "../utils/types";
import { errorToaster, measurementUnits, renderDeviceStatus, renderLastMeasurementValue, renderObjectLinkInDevices } from "../utils/utils";

const Devices = () => {
  const {projectId, project, userSettings} = useProjectContext();
  const tableDetails = useUserTableDetails(userSettings, "Devices");
  const [tableOptions, setTableOptions] = useTableFilter("Devices", {
    sortField: "identifier",
    reqSortField: "identifier",
    sortOrder: 1,
  });

  const [getFilteredDevicesData, { data: deviceData, loading }] = useGetDevicesPagedLazyQuery({
    onError: errorToaster,
  });

  useEffect(() => {
    if(userSettings) {
      getFilteredDevicesData({
        variables: {
          input: {
            projectId,
            take: tableDetails?.rows || 10,
            skip: tableOptions.first,
            sort: [
              {
                property: tableOptions.reqSortField,
                direction: tableOptions.sortOrder === 1 ? SortDirection.Asc : SortDirection.Desc,
              },
            ],
            andFilters: tableOptions.andFilters,
            filter: tableOptions.filter,
          },
        },
      });
    }
  }, [userSettings, tableDetails, projectId, tableOptions, getFilteredDevicesData]);

  const edit = (rowData: MdmDeviceDto) => (
    <Link to={`${rowData.id}`} title={T("mdm.device-info")}>
      {rowData.identifier}
    </Link>
  );
  
  const handleLastMeasurementDate = (rowData: MdmDeviceDto) =>
    rowData.status.lastMeasurementDate &&
    parseToLocalZoneDateTime(rowData.status.lastMeasurementDate).format(DEFAULT_DATE_FORMATTER);

  const tableData = useMemo(
    () => [...(deviceData?.mdmDevicesByProjectIdPaged.items || [])] as MdmDeviceDto[],
    [deviceData?.mdmDevicesByProjectIdPaged.items]
  );

  const measurementKeys = useMemo(
    () => [
      ...(project?.mdmProjectById.deviceConfigs?.reduce((acc, curr) => {
        if (curr?.measurementMappings) {
          curr.measurementMappings.forEach(m => acc.add(m?.key!));
        }
        return acc;
      }, new Set<string>()) ?? []),
    ],
    [project?.mdmProjectById.deviceConfigs]
  );

  const columns: TableColumn<MdmDeviceDto>[] = useMemo(
    () => [
      {
        field: "identifier",
        header: T("word.identifier"),
        body: edit,
        sortable: true,
        columnKey: "identifier",
      },
      {
        field: "objectRelation.mdmObject.identifier",
        header: T("mdm.object-name"),
        body: rowData => renderObjectLinkInDevices(rowData, projectId!),
        sortable: true,
        columnKey: "Object Code",
      },
      {
        field: "objectRelation.mdmObject.address.fullAddress",
        header: T("word.address"),
        sortable: true,
        columnKey: "Object Address",
      },
      {
        field: "objectRelation.roleInObject",
        header: T("word.type"),
        body: rowData =>
          project?.mdmProjectById.configuration?.deviceConfiguration.measurementTypes?.find(
            type => type?.id === rowData.objectRelation?.roleInObject
          )?.title ?? "",
        sortable: true,
        columnKey: "Type",
      },
      {
        field: "config.title",
        header: T("word.config"),
        sortable: true,
        columnKey: "Config",
      },
      {
        field: "status.status",
        header: T("word.status"),
        body: renderDeviceStatus,
        sortable: true,
        hideInFile: true,
        columnKey: "Status",
      },
      {
        field: "datasetCount",
        header: T("word.datasets"),
        sortable: true,
        columnKey: "Datasets",
      },
      {
        field: "status.lastMeasurementDate",
        header: T("mdm.last-measurement-date"),
        body: handleLastMeasurementDate,
        sortable: true,
        columnKey: "Last Measurement Date",
      },
      ...measurementKeys
        .sort((a, b) => {
          const indexA = measurementOrder.indexOf(a);
          const indexB = measurementOrder.indexOf(b);
          if (indexA === -1 && indexB === -1) return a.localeCompare(b);
          if (indexA === -1) return 1;
          if (indexB === -1) return -1;
          return indexA - indexB;
        })
        .map(key => ({
          field: `lastMeasurements.${key}` as keyof MdmDeviceDto,
          header: `${key}${measurementUnits(key) ? `, ${measurementUnits(key)}` : ""}`,
          body: (data: MdmDeviceDto) => renderLastMeasurementValue(data, key),
          columnKey: key,
          sortable: true,
        })),
    ],
    [
      measurementKeys,
      project?.mdmProjectById.configuration?.deviceConfiguration.measurementTypes,
      projectId,
    ]
  );

  const formattedData = useMemo(
    () =>
      tableData.map(
        row =>
          ({
            ...row,
            lastMeasurements: row.lastMeasurements.reduce(
              (acc, m) => ({ ...acc, [m?.mapping as string]: m?.value }),
              {}
            ),
          }) as MdmDeviceDto
      ),
    [tableData]
  );

  return (
    <TabView panelContainerClassName="p-0 sm:p-4">
      <TabPanel header={T("word.table")}>
        <ObjectAndDeviceTableWrapper
          columns={columns}
          data={formattedData}
          containerName="MdmDevice"
          tableName="Devices"
          loading={loading}
          setTableOptions={setTableOptions}
          tableOptions={tableOptions}
          totalItems={deviceData?.mdmDevicesByProjectIdPaged.pageInfo?.total || 0}
          getQueryData={getFilteredDevicesData}
        />
      </TabPanel>
      <TabPanel header={T("word.map")}>
        <DeviceProvider tableOptions={tableOptions} records={deviceData?.mdmDevicesByProjectIdPaged.pageInfo?.total ?? 100} />
      </TabPanel>
    </TabView>
  );
};

export default Devices;

type DeviceProviderProps = {
  tableOptions: DataTablePFSEvent;
  records: number;
};
const DeviceProvider: React.FC<DeviceProviderProps> = ({tableOptions,records}) => {
  const {projectId} = useProjectContext();

  const { data, loading } = useGetDevicesPagedQuery({
    variables: {
      input: {
        projectId,
        skip: 0,
        take: records,
        andFilters: tableOptions.andFilters,
        filter: tableOptions.filter,
      },
    },
  });

  return (
    <MapWrapper>
      {loading && <ProgressSpinner className='mt-8 w-full' style={{zIndex: 400}}/>}
      <ObjectMapContent objects={[...new Set(data?.mdmDevicesByProjectIdPaged.items.map(t => t?.objectRelation?.mdmObject! as MdmObjectDto))]} />
    </MapWrapper>
  );
}